import { supabase } from '../lib/supabase';

export const superAdminService = {
  // Dashboard Statistics
  async getDashboardStats() {
    try {
      const [orgsResult, usersResult, subscriptionsResult] = await Promise.all([
        supabase?.from('organizations')?.select('id', { count: 'exact', head: true }),
        supabase?.from('user_profiles')?.select('id', { count: 'exact', head: true }),
        supabase?.from('subscriptions')?.select('status, price_amount')?.eq('status', 'active')
      ]);

      const totalOrganizations = orgsResult?.count || 0;
      const totalUsers = usersResult?.count || 0;
      const activeSubscriptions = subscriptionsResult?.data?.length || 0;
      const monthlyRevenue = subscriptionsResult?.data?.reduce((sum, sub) => sum + (sub?.price_amount || 0), 0) || 0;

      return {
        totalOrganizations,
        totalUsers,
        activeSubscriptions,
        monthlyRevenue
      };
    } catch (error) {
      throw new Error('Failed to fetch dashboard statistics');
    }
  },

  // Organizations
  async getOrganizations() {
    try {
      const { data, error } = await supabase?.from('organizations')?.select(`
          *,
          subscriptions (
            plan_name,
            status,
            price_amount
          )
        `)?.order('created_at', { ascending: false });

      if (error) throw error;

      // Get member counts for each organization
      const orgsWithCounts = await Promise.all(
        data?.map(async (org) => {
          const { count } = await supabase?.from('organization_members')?.select('id', { count: 'exact', head: true })?.eq('organization_id', org?.id);

          return {
            ...org,
            member_count: count || 0,
            subscription: org?.subscriptions?.[0] || null
          };
        }) || []
      );

      return orgsWithCounts;
    } catch (error) {
      throw new Error('Failed to fetch organizations');
    }
  },

  async updateOrganizationStatus(orgId, status) {
    try {
      const { error } = await supabase?.from('organizations')?.update({ status, updated_at: new Date()?.toISOString() })?.eq('id', orgId);

      if (error) throw error;
    } catch (error) {
      throw new Error('Failed to update organization status');
    }
  },

  // Users
  async getUsers() {
    try {
      const { data, error } = await supabase?.from('user_profiles')?.select('*')?.order('created_at', { ascending: false });

      if (error) throw error;

      // Get organization counts for each user
      const usersWithCounts = await Promise.all(
        data?.map(async (user) => {
          const { count } = await supabase?.from('organization_members')?.select('id', { count: 'exact', head: true })?.eq('user_id', user?.id);

          return {
            ...user,
            organization_count: count || 0
          };
        }) || []
      );

      return usersWithCounts;
    } catch (error) {
      throw new Error('Failed to fetch users');
    }
  },

  async updateUserRole(userId, role) {
    try {
      const { error } = await supabase?.from('user_profiles')?.update({ role, updated_at: new Date()?.toISOString() })?.eq('id', userId);

      if (error) throw error;
    } catch (error) {
      throw new Error('Failed to update user role');
    }
  },

  async updateUserStatus(userId, isActive) {
    try {
      const { error } = await supabase?.from('user_profiles')?.update({ is_active: isActive, updated_at: new Date()?.toISOString() })?.eq('id', userId);

      if (error) throw error;
    } catch (error) {
      throw new Error('Failed to update user status');
    }
  },

  // Billing & Revenue
  async getBillingStats() {
    try {
      const { data: subscriptions, error } = await supabase?.from('subscriptions')?.select('price_amount, status');

      if (error) throw error;

      const totalRevenue = subscriptions?.reduce((sum, sub) => sum + (sub?.price_amount || 0), 0) || 0;
      const activeSubscriptions = subscriptions?.filter(sub => sub?.status === 'active')?.length || 0;
      const monthlyRecurring = subscriptions
        ?.filter(sub => sub?.status === 'active')
        ?.reduce((sum, sub) => sum + (sub?.price_amount || 0), 0) || 0;
      const averageRevenue = activeSubscriptions > 0 ? monthlyRecurring / activeSubscriptions : 0;

      return {
        totalRevenue,
        monthlyRecurring,
        activeSubscriptions,
        averageRevenue
      };
    } catch (error) {
      throw new Error('Failed to fetch billing statistics');
    }
  },

  async getRevenueData(timeRange = '30d') {
    try {
      // For demo purposes, return mock revenue data
      // In a real app, this would query actual revenue data based on timeRange
      const days = timeRange === '7d' ? 7 : timeRange === '30d' ? 30 : 90;
      const mockData = [];

      for (let i = days - 1; i >= 0; i--) {
        const date = new Date();
        date?.setDate(date?.getDate() - i);
        mockData?.push({
          date: date?.toISOString()?.split('T')?.[0],
          amount: Math.floor(Math.random() * 10000) + 5000 // Random revenue between 5000-15000
        });
      }

      return mockData;
    } catch (error) {
      throw new Error('Failed to fetch revenue data');
    }
  },

  async getSubscriptionsOverview() {
    try {
      const { data, error } = await supabase?.from('subscriptions')?.select('plan_name')?.eq('status', 'active');

      if (error) throw error;

      // Group by plan name and count
      const planCounts = data?.reduce((acc, sub) => {
        const plan = sub?.plan_name || 'Unknown';
        acc[plan] = (acc?.[plan] || 0) + 1;
        return acc;
      }, {}) || {};

      return Object.entries(planCounts)?.map(([plan_name, count]) => ({
        plan_name,
        count
      }));
    } catch (error) {
      throw new Error('Failed to fetch subscriptions overview');
    }
  },

  async getUsageMetrics() {
    try {
      const { data, error } = await supabase?.from('billing_usage')?.select(`
          *,
          organization:organizations(name)
        `)?.order('usage_count', { ascending: false })?.limit(10);

      if (error) throw error;

      return data?.map(usage => ({
        ...usage,
        organization_name: usage?.organization?.name || 'Unknown'
      })) || [];
    } catch (error) {
      throw new Error('Failed to fetch usage metrics');
    }
  },

  // Feature Flags
  async getFeatureFlags() {
    try {
      const { data, error } = await supabase?.from('feature_flags')?.select('*')?.order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      throw new Error('Failed to fetch feature flags');
    }
  },

  async createFeatureFlag(flagData) {
    try {
      const { error } = await supabase?.from('feature_flags')?.insert([flagData]);

      if (error) throw error;
    } catch (error) {
      throw new Error('Failed to create feature flag');
    }
  },

  async toggleFeatureFlag(flagId, isActive) {
    try {
      const { error } = await supabase?.from('feature_flags')?.update({
        is_active: isActive,
        updated_at: new Date()?.toISOString()
      })?.eq('id', flagId);

      if (error) throw error;
    } catch (error) {
      throw new Error('Failed to toggle feature flag');
    }
  },

  // Audit Logs
  async getAuditLogs(filters = {}) {
    try {
      let query = supabase?.from('audit_logs')?.select(`
          *,
          organization:organizations(name),
          user_profile:user_profiles(email)
        `)?.order('created_at', { ascending: false })?.limit(100);

      // Apply filters
      if (filters?.action && filters?.action !== 'all') {
        query = query?.ilike('action', `%${filters?.action}%`);
      }

      if (filters?.dateRange) {
        const days = parseInt(filters?.dateRange?.replace('d', ''));
        const cutoffDate = new Date();
        cutoffDate?.setDate(cutoffDate?.getDate() - days);
        query = query?.gte('created_at', cutoffDate?.toISOString());
      }

      const { data, error } = await query;

      if (error) throw error;

      return data?.map(log => ({
        ...log,
        organization_name: log?.organization?.name || null,
        user_email: log?.user_profile?.email || null
      })) || [];
    } catch (error) {
      throw new Error('Failed to fetch audit logs');
    }
  },

  // System Settings (Mock implementation)
  async getSystemSettings() {
    try {
      // In a real app, this would fetch from a system_settings table
      // For now, return default settings
      return {
        systemName: 'Multi-Tenant SaaS Platform',
        systemDescription: 'Professional SaaS application with multi-tenant architecture',
        maintenanceMode: false,
        allowNewRegistrations: true,
        defaultUserRole: 'org_user',
        maxOrganizationsPerUser: 3,
        sessionTimeout: 30,
        passwordMinLength: 8,
        requirePasswordSpecialChars: true,
        emailNotifications: true,
        auditLogRetention: 90,
        backupFrequency: 'daily'
      };
    } catch (error) {
      throw new Error('Failed to fetch system settings');
    }
  },

  async updateSystemSettings(settings) {
    try {
      // In a real app, this would update a system_settings table
      // For now, simulate a successful update
      await new Promise(resolve => setTimeout(resolve, 1000));
      return settings;
    } catch (error) {
      throw new Error('Failed to update system settings');
    }
  }
};
